import React from 'react';
import {renderMarkdownLite} from './markdownLite';

interface AIRewriteModalProps {
    show: boolean;
    rewriteResult: {rewrittenPrompt: string; originalPrompt: string} | null;
    onAccept: () => void;
    onReject: () => void;
}

// AI Rewrite Result modal. Extracted from PromptManager.tsx
// (deepcontext-ityf decomposition); follows the locked modal spec in
// deepcontext-pnxk — 18px-radius surface, the original/enhanced boxes as
// canvas insets, ghost + gradient footer.
const AIRewriteModal: React.FC<AIRewriteModalProps> = ({show, rewriteResult, onAccept, onReject}) => {
    if (!show || !rewriteResult) return null;

    const hasOriginalPrompt = rewriteResult.originalPrompt.trim().length > 0;

    return (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-black/50">
            <div
                data-testid="ai-rewrite-surface"
                className="relative top-20 mx-auto w-[800px] rounded-[18px] border border-line bg-surface p-5 shadow-[0_18px_60px_rgba(0,0,0,0.35)]"
            >
                <h3 className="mb-3.5 text-[15px] font-extrabold text-ink">
                    AI-Enhanced Brand Blueprint
                </h3>
                <div className="space-y-3">
                    {hasOriginalPrompt && (
                        <div>
                            <label className="mb-1 block text-xs font-bold uppercase tracking-[0.5px] text-ink-muted">
                                Original Prompt
                            </label>
                            <div
                                data-testid="ai-rewrite-original"
                                className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded-xl border border-line bg-canvas px-3.5 py-3 text-sm leading-relaxed text-ink">
                                {rewriteResult.originalPrompt}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="mb-1 block text-xs font-bold uppercase tracking-[0.5px] text-ink-muted">
                            AI-Enhanced Prompt
                        </label>
                        <div
                            data-testid="ai-rewrite-enhanced"
                            className="max-h-64 overflow-y-auto rounded-xl border border-line bg-canvas px-3.5 py-3 text-sm leading-relaxed text-ink">
                            {renderMarkdownLite(rewriteResult.rewrittenPrompt)}
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onReject}
                        className="rounded-full bg-ghost-bg px-4 py-2 text-sm font-bold text-ink hover:opacity-80"
                    >
                        Keep Original
                    </button>
                    <button
                        type="button"
                        onClick={onAccept}
                        className="rounded-full bg-gradient-accent px-4 py-2 text-sm font-bold text-white hover:opacity-90"
                    >
                        Use AI Version
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIRewriteModal;
